// src/components/Admin/BoardGroupManager/CategoryList.jsx
import { useState } from 'react';
import {
    TableContainer, Paper, Table, TableHead, TableRow, TableCell, TableBody,
    IconButton, Snackbar, Alert, Chip, Accordion, AccordionSummary,
    AccordionDetails, Typography, Box, Tooltip, useMediaQuery,
} from '@mui/material';
import {
    ArrowUpward, ArrowDownward, Edit, Delete,
    Visibility as VisibilityIcon, VisibilityOff as VisibilityOffIcon,
    ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';

import {
    moveBoardGroup,
    deleteBoardGroup,
    editBoardGroup,
} from '../../../api/admin';
import { useCategory } from '../../../contexts/CategoryContext';

/** props
 * groups      : [{ id, name, emoji, is_active, order, categories: [...] }]
 * onEdit      : (groupObj) => void
 * reload      : () => void     // 목록 재로딩
 */
export default function GroupList({ groups = [], onEdit, reload }) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [snackbar, setSb] = useState({ open: false, msg: '' });
    const notify = (msg) => setSb({ open: true, msg });
    const { reloadCategories } = useCategory();

    /* ───────── 공통 액션 ───────── */
    const handleMove = (id, dir) =>
        moveBoardGroup(id, dir)
            .then(() => { reload(); notify('순서가 바뀌었습니다!'); })
            .catch(() => notify('순서 변경 실패'));

    const handleDelete = (id) => {
        if (!window.confirm('정말 삭제할까요?')) return;
        deleteBoardGroup(id)
            .then(() => { reload(); notify('그룹이 삭제되었습니다.'); })
            .catch(() => notify('삭제 실패'));
    };

    const handleToggleActive = (grp) =>
        editBoardGroup(grp.id, { is_active: !grp.is_active })
            .then(() => {
                reload();              // 로컬 리스트 새로고침
                reloadCategories();    // ★ 사이드바 & 전체 컨텍스트 갱신
                notify(grp.is_active ? '숨김 처리' : '노출 처리');
            })
            .catch(() => notify('상태 변경 실패'));

    /* ──────────────────────────── Mobile UI (Accordion) ──────────────────────────── */
    if (isMobile) {
        return (
            <>
                <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {groups.map((g) => (
                        <Accordion key={g.id}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    <Typography fontWeight="bold">
                                        {g.emoji} {g.name}
                                    </Typography>
                                    <Chip
                                        label={g.is_active ? '노출중' : '숨김'}
                                        size="small"
                                        color={g.is_active ? 'success' : 'default'}
                                        icon={g.is_active ? <VisibilityIcon /> : <VisibilityOffIcon />}
                                        onClick={(e) => { e.stopPropagation(); handleToggleActive(g); }}
                                        clickable
                                        sx={{ alignSelf: 'flex-start' }}
                                    />
                                </Box>
                            </AccordionSummary>

                            <AccordionDetails>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    포함 게시판 수: {g.categories?.length ?? 0}
                                </Typography>

                                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', mb: 1 }}>
                                    <Tooltip title="위로 이동">
                                        <IconButton size="small" onClick={() => handleMove(g.id, 'up')}>
                                            <ArrowUpward fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="아래로 이동">
                                        <IconButton size="small" onClick={() => handleMove(g.id, 'down')}>
                                            <ArrowDownward fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </Box>

                                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                                    <Tooltip title="수정">
                                        <IconButton size="small" onClick={() => onEdit(g)}>
                                            <Edit fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="삭제">
                                        <IconButton size="small" onClick={() => handleDelete(g.id)}>
                                            <Delete fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            </AccordionDetails>
                        </Accordion>
                    ))}
                </Box>

                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={3000}
                    onClose={() => setSb({ ...snackbar, open: false })}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                >
                    <Alert severity="success" onClose={() => setSb({ ...snackbar, open: false })} sx={{ width: '100%' }}>
                        {snackbar.msg}
                    </Alert>
                </Snackbar>
            </>
        );
    }

    /* ──────────────────────────── Desktop UI (Table) ──────────────────────────── */
    return (
        <>
            <TableContainer component={Paper} sx={{ mt: 2 }}>
                <Table size="small" stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell align="center" width={80}>순서</TableCell>
                            <TableCell align="center" width={80}>이모지</TableCell>
                            <TableCell>그룹명</TableCell>
                            <TableCell align="center">게시판 수</TableCell>
                            <TableCell align="center">상태</TableCell>
                            <TableCell align="center" width={120}>관리</TableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {groups.map((g) => (
                            <TableRow key={g.id} hover>
                                <TableCell align="center">
                                    <IconButton size="small" onClick={() => handleMove(g.id, 'up')}>
                                        <ArrowUpward fontSize="inherit" />
                                    </IconButton>
                                    <IconButton size="small" onClick={() => handleMove(g.id, 'down')}>
                                        <ArrowDownward fontSize="inherit" />
                                    </IconButton>
                                </TableCell>

                                <TableCell align="center">{g.emoji}</TableCell>
                                <TableCell>{g.name}</TableCell>
                                <TableCell align="center">{g.categories?.length ?? 0}</TableCell>

                                <TableCell align="center">
                                    <Chip
                                        label={g.is_active ? '노출중' : '숨김'}
                                        size="small"
                                        color={g.is_active ? 'success' : 'default'}
                                        icon={g.is_active ? <VisibilityIcon /> : <VisibilityOffIcon />}
                                        onClick={() => handleToggleActive(g)}
                                        clickable
                                    />
                                </TableCell>

                                <TableCell align="center">
                                    <IconButton size="small" onClick={() => onEdit(g)}>
                                        <Edit fontSize="inherit" />
                                    </IconButton>
                                    <IconButton size="small" onClick={() => handleDelete(g.id)}>
                                        <Delete fontSize="inherit" />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSb({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity="success" onClose={() => setSb({ ...snackbar, open: false })} sx={{ width: '100%' }}>
                    {snackbar.msg}
                </Alert>
            </Snackbar>
        </>
    );
}
